import { t } from 'i18next'

import {
  TransactionDirection,
  TransactionDto,
  TransactionStateHistoryDto,
} from '@model/TransactionDto'
import { TransactionStatus } from '@model/TransactionStatus'
import { CurrencyType, WalletTypeEnum } from '@model/WalletDto'

import { isOne } from './validations'

/**
 * Transaction types used across Wallet and Trading Account (TA) operations.
 */
export enum TransactionType {
  /** Wallet → Wallet. */
  WalletTransfer = 100,

  /** Payment Agent → Client Wallet (Wallet ↔ Wallet). */
  DepositFromPaymentAgent = 112,

  /** Client Wallet → Payment Agent (Wallet ↔ Wallet). */
  WithdrawToPaymentAgent = 114,

  /** PSP → Wallet. */
  WalletDeposit = 200,

  /** Wallet → PSP. */
  WalletWithdrawal = 300,

  /** Trading Account → Wallet. */
  TransferFromTradingAccount = 400,

  /** Trading Account → Wallet (inactivity). */
  InactivityTransfer = 450,

  /** Wallet → Trading Account. */
  TransferToTradingAccount = 500,

  /** Trading Account → Trading Account (balance conversion). */
  TradingAccountBalanceConversion = 520,

  /** Single Wallet (credit or debit). */
  ManualAdjustment = 610,

  /** Single Wallet (credit). */
  Refund = 630,

  /** Single Wallet (credit). */
  WelcomeAccountReward = 640,

  /** Single Wallet (credit). */
  TradingIssueCompensation = 650,

  /** Single Wallet (credit). */
  Bonus = 660,

  /** Single Wallet (debit and credit). */
  InterEntityTransfer = 662,

  /** Single Wallet (debit and credit). */
  UnallocatedFundsTransfer = 664,

  /** Single Wallet (credit). */
  ArchivedAccountTransfer = 668,

  /** Single Wallet (credit). */
  IBCommission = 670,

  /** Single Wallet (credit). */
  MIBCommission = 675,

  /** Single Wallet (credit). */
  MultiTierIBCommission = 676,

  /** Reversal of wallet transactions. */
  Reverse = 680,

  /** Single Wallet (debit and credit). */
  IBRebate = 682,

  /** Single Wallet (debit and credit). */
  IBRebateAdjustment = 683,

  /** Single Wallet (credit). */
  BankFeesRefund = 685,

  /** Single Wallet (debit and credit). */
  DeactivatedAccountWalletTransfer = 686,

  /** Single Wallet (debit). */
  FeeDeduction = 688,

  /** Single Wallet (credit). */
  CopyTraderFees = 690,

  /** Single Wallet (credit). Seems only for IB wallets so far. */
  LoyaltyRewardPayout = 695,

  /** Single Wallet (credit). */
  MonthlyInterestTransfer = 696,

  /** Single Wallet (debit). */
  InactivityFee = 698,

  /** List of single-leg Trading Account transactions (ex NegativeBalanceRefund). */
  NegativeEquityRefund = 710,

  /** List of single-leg Trading Account transactions. */
  ZuluTradeManagementFee = 720,

  /** List of single-leg Trading Account transactions. */
  ZuluTradeSubscriptionFee = 730,

  /** List of single-leg Trading Account transactions. */
  ManualTradingAccountAdjustment = 740,

  /** List of single-leg Trading Account transactions. */
  CampaignBonus = 750,

  /** List of single-leg Trading Account transactions. */
  PelicanPerformanceFee = 764,

  /**
   * List of single-leg Trading Account transactions.
   * @see https://tickmill.atlassian.net/browse/BOS-575
   */
  SocialTradingReward = 765,

  /** List of single-leg Trading Account transactions. */
  PelicanMonthlyFee = 768,

  /** List of single-leg Trading Account transactions. */
  SubscriptionFee = 770,

  /** List of single-leg Trading Account transactions. */
  CopyTraderFeesTa = 775,

  /** List of single-leg Trading Account transactions. */
  DividendAdjustment = 780,

  /** List of single-leg Trading Account transactions. */
  RolloverAdjustment = 790,

  /** List of single-leg Trading Account transactions. */
  SocialTradingPerformanceFee = 795,

  /** List of single-leg Trading Account transactions. */
  SocialTradingManagementFee = 796,

  /** List of single-leg Trading Account transactions. */
  SocialTradingRegistrationFee = 797,

  /** List of single-leg Trading Account transactions. */
  SocialTradingPlatformFee = 798,

  /** List of single-leg Trading Account transactions. */
  SocialTradingAgentFee = 799,
}

const getWalletText = () => ` ${t('Wallet.Wallet')}`

export function getTransactionType(id: number): TransactionType {
  const transactionTypeValues = Object.values(TransactionType).filter(
    (value): value is TransactionType => typeof value === 'number'
  )

  return transactionTypeValues.includes(id as TransactionType)
    ? (id as TransactionType)
    : TransactionType.Bonus
}

export function getCurrencySymbol(type: CurrencyType): string {
  switch (type) {
    case 'GBP':
      return '£'
    case 'EUR':
      return '€'
    case 'PLN':
      return 'zł'
    case 'USD':
      return '$'
    case 'CHF':
      return '₣'
    case 'HKD':
      return 'HK$'
    case 'SGD':
      return 'S$'
    case 'AUD':
      return 'AU$'
    case 'CAD':
      return 'C$'
    case 'JPY':
      return 'J¥'
    case 'CNH':
      return 'C¥'
    case 'MYR':
      return 'RM'
    case 'ZAR':
      return 'R'
    case 'BTC':
      return '₿'
    case 'CRC':
      return '₡'
    case 'CLP':
      return 'CLP$'
    case 'AED':
      return 'د.إ'
    case 'ARS':
      return '$'
    case 'BDT':
      return '৳'
    case 'BHD':
      return 'د.ب'
    case 'BWP':
      return 'P'
    case 'BRL':
      return 'R$'
    case 'CNY':
      return '¥'
    case 'COP':
      return '$'
    case 'GHS':
      return '₵'
    case 'IDR':
      return 'Rp'
    case 'INR':
      return '₹'
    case 'JOD':
      return 'د.ا'
    case 'KES':
      return 'KSh'
    case 'KGS':
      return 'сом'
    case 'KRW':
      return '₩'
    case 'KWD':
      return 'د.ك'
    case 'KZT':
      return '₸'
    case 'LAK':
      return '₭'
    case 'MAD':
      return 'د.م.'
    case 'MXN':
      return '$'
    case 'NGN':
      return '₦'
    case 'NZD':
      return 'NZ$'
    case 'OMR':
      return '﷼'
    case 'PEN':
      return 'S/'
    case 'PHP':
      return '₱'
    case 'PKR':
      return '₨'
    case 'QAR':
      return '﷼'
    case 'RUB':
      return '₽'
    case 'SAR':
      return '﷼'
    case 'THB':
      return '฿'
    case 'TND':
      return 'د.ت'
    case 'TRY':
      return '₺'
    case 'TWD':
      return 'NT$'
    case 'TZS':
      return 'TSh'
    case 'UGX':
      return 'USh'
    case 'UZS':
      return 'soʻm'
    case 'VND':
      return '₫'
    default:
      return type
  }
}

const CREDIT_TRANSACTION_TYPES: TransactionType[] = [
  TransactionType.WalletDeposit,
  TransactionType.DepositFromPaymentAgent,
  TransactionType.TransferFromTradingAccount,
  TransactionType.InactivityTransfer,
  TransactionType.Refund,
  TransactionType.WelcomeAccountReward,
  TransactionType.TradingIssueCompensation,
  TransactionType.Bonus,
  TransactionType.IBCommission,
  TransactionType.MIBCommission,
  TransactionType.MultiTierIBCommission,
  TransactionType.BankFeesRefund,
  TransactionType.CopyTraderFees,
  TransactionType.LoyaltyRewardPayout,
  TransactionType.MonthlyInterestTransfer,
  TransactionType.ArchivedAccountTransfer,
  TransactionType.IBRebate,
  TransactionType.NegativeEquityRefund,
  TransactionType.CampaignBonus,
  TransactionType.SocialTradingReward,
]

const DEBIT_TRANSACTION_TYPES: TransactionType[] = [
  TransactionType.WalletWithdrawal,
  TransactionType.TransferToTradingAccount,
  TransactionType.WithdrawToPaymentAgent,
  TransactionType.FeeDeduction,
  TransactionType.InactivityFee,
  TransactionType.SubscriptionFee,
  TransactionType.ZuluTradeSubscriptionFee,
  TransactionType.ZuluTradeManagementFee,
  TransactionType.PelicanMonthlyFee,
  TransactionType.PelicanPerformanceFee,
  TransactionType.IBRebate,
]

export const isTransactionPositive = (transaction: TransactionDto, clientId?: string): boolean => {
  const calculatedDir = getTransactionDirectionCredit(transaction.type.id, transaction, clientId)
  if (calculatedDir !== null) return calculatedDir
  else if (DEBIT_TRANSACTION_TYPES.includes(transaction.type.id)) return false
  return true
}

export const getTransactionAmountSign = (
  type: TransactionType,
  isReversed: boolean,
  transaction?: TransactionDto,
  clientId?: string
): '+' | '-' => {
  let isCredit: boolean | undefined
  if (CREDIT_TRANSACTION_TYPES.includes(type)) isCredit = true
  else if (DEBIT_TRANSACTION_TYPES.includes(type)) isCredit = false
  else isCredit = getTransactionDirectionCredit(type, transaction, clientId)

  if (isReversed) isCredit = !isCredit
  return isCredit ? '+' : '-'
}

const getTransactionDirectionCredit = (
  type: TransactionType,
  transaction: TransactionDto | undefined,
  clientId?: string
): boolean => {
  const clientWalletTransactions = clientId
    ? transaction?.walletTransactions.filter((x) => x.wallet?.client?.id === clientId) || []
    : []

  if (type === TransactionType.WalletTransfer && clientWalletTransactions.length !== 1) {
    const creditWalletTransaction = transaction?.walletTransactions.find(
      (x) => x.directionType.name === 'Credit'
    )
    if (creditWalletTransaction) return true
  }

  if (type === TransactionType.TradingAccountBalanceConversion) {
    const clientTradingAccountTransactions = clientId
      ? transaction?.tradingAccountTransactions.filter((x) => String(x.client?.id) === clientId) ||
        []
      : []

    if (clientTradingAccountTransactions.length !== 1) {
      const creditTradingAccountTransaction = transaction?.tradingAccountTransactions.find(
        (x) => x.directionType.name === 'Credit'
      )
      if (creditTradingAccountTransaction) return true
    }
  }

  const walletTransaction =
    clientWalletTransactions[0] ||
    (transaction?.walletTransactions.length === 1 ? transaction.walletTransactions[0] : undefined)

  if (walletTransaction?.directionType.name) {
    return walletTransaction.directionType.name === 'Credit'
  }

  const tradingAccountTransaction =
    (clientId
      ? transaction?.tradingAccountTransactions.find((x) => String(x.client?.id) === clientId)
      : undefined) ||
    (transaction?.tradingAccountTransactions.length === 1
      ? transaction.tradingAccountTransactions[0]
      : undefined)

  if (tradingAccountTransaction?.directionType.name) {
    return tradingAccountTransaction.directionType.name === 'Credit'
  }

  return true
}

export type TransactionContext = 'client' | 'ib' | 'paymentAgent'

export interface TransactionFields {
  from: string
  to: string
  currency: CurrencyType | ''
  amount: number
  rate: number
}

export interface TransactionFieldsContextOptions {
  isArabic: boolean
  authId?: string
  isDefaultCFDProductType?: boolean
  isReversed?: boolean
  walletId?: string
}

export const getTransactionFieldsByContext = (
  context: TransactionContext,
  type: TransactionType,
  transaction: TransactionDto,
  { isArabic, authId, isDefaultCFDProductType = false, isReversed }: TransactionFieldsContextOptions
): TransactionFields => {
  switch (context) {
    case 'ib':
      return getIBTransactionFieldsByTransactionType(type, transaction, isArabic, authId)
    case 'paymentAgent':
      return getPATransactionFieldsByTransactionType(
        type,
        transaction,
        isArabic,
        authId,
        isReversed
      )
    case 'client':
    default:
      return getClientTransactionFieldsByTransactionType(
        type,
        transaction,
        isDefaultCFDProductType,
        isArabic,
        authId
      )
  }
}

export interface TransactionDisplay {
  transactionType: TransactionType
  isReversed: boolean
  fields: TransactionFields
  amountSign: '+' | '-'
}

export const getTransactionDisplay = (
  transaction: TransactionDto,
  context: TransactionContext,
  options: TransactionFieldsContextOptions
): TransactionDisplay => {
  const supportsReverse = context === 'paymentAgent'
  const isReversed = supportsReverse && transaction.type.id === TransactionType.Reverse
  const reversedTypeId = transaction.reversedTransactions?.[0]?.type?.id
  const transactionType =
    isReversed && reversedTypeId
      ? getTransactionType(reversedTypeId)
      : getTransactionType(transaction.type.id)

  let fields = getTransactionFieldsByContext(context, transactionType, transaction, {
    ...options,
    isReversed,
  })
  if (isReversed) {
    fields = { ...fields, from: fields.to, to: fields.from }
  }

  const walletTransaction = options.walletId
    ? transaction.walletTransactions.find((item) => item.wallet.id === options.walletId)
    : undefined

  if (walletTransaction) {
    fields = {
      ...fields,
      currency: walletTransaction.wallet.currency.id,
      amount: walletTransaction.amount,
      rate: walletTransaction.currencyRate,
    }
  }

  const amountSign = walletTransaction
    ? walletTransaction.directionType.id === TransactionDirection.Credit
      ? '+'
      : walletTransaction.directionType.id === TransactionDirection.Debit
      ? '-'
      : getTransactionAmountSign(transactionType, isReversed, transaction, options.authId)
    : getTransactionAmountSign(transactionType, isReversed, transaction, options.authId)

  return { transactionType, isReversed, fields, amountSign }
}

const getCurrency = (currencyId: CurrencyType | undefined, isArabic: boolean): string => {
  if (!currencyId) {
    return ''
  }
  return isArabic ? getCurrencySymbol(currencyId as CurrencyType) : currencyId
}

// REFERENCE - CAR-65 (https://jira.tickmill.com/browse/CAR-65)
export function getClientTransactionFieldsByTransactionType(
  type: TransactionType,
  transaction: TransactionDto,
  isDefaultCFDProductType: boolean,
  isArabic: boolean,
  clientId?: string
): TransactionFields {
  switch (type) {
    case TransactionType.WalletTransfer:
      const fromWallet = transaction.walletTransactions.find(
        (x) => x.directionType.name === 'Debit'
      )
      const toWallet = transaction.walletTransactions.find((x) => x.directionType.name === 'Credit')
      return {
        from:
          fromWallet?.wallet.walletType.id === WalletTypeEnum.IB
            ? t('Wallet.IB Currency Wallet', {
                walletCurrencyId: getCurrency(fromWallet?.wallet.currency.id, isArabic),
              })
            : t('Wallet.Currency Wallet', {
                walletCurrencyId: getCurrency(fromWallet?.wallet.currency.id, isArabic),
              }),

        to:
          t('Wallet.Currency Wallet', {
            walletCurrencyId: getCurrency(toWallet?.wallet.currency.id, isArabic),
          }) || '',
        currency: toWallet?.wallet.currency.id || '',
        amount: toWallet?.amount || 0,
        rate: fromWallet?.currencyRate || 0,
      }
    case TransactionType.DepositFromPaymentAgent:
      const paWalletTransaction = transaction.walletTransactions.find(
        (x) => !!x.paymentAgentTransaction
      )
      const clientWalletTransaction = transaction.walletTransactions.find(
        (x) => !x.paymentProviderTransaction
      )
      return {
        from: paWalletTransaction?.paymentAgentTransaction?.paymentAgent.name || '',
        to: t('Wallet.Currency Wallet', {
          walletCurrencyId: getCurrency(clientWalletTransaction?.wallet.currency.id, isArabic),
        }),
        currency: clientWalletTransaction?.wallet.currency.id || '',
        amount: clientWalletTransaction?.amount || 0,
        rate: clientWalletTransaction?.currencyRate || 0,
      }
    case TransactionType.WithdrawToPaymentAgent:
      const paWalletTransactionWithdrawalType = transaction.walletTransactions.find(
        (x) => !!x.paymentAgentTransaction
      )
      const clientWalletTransactionWithdrawalType = transaction.walletTransactions.find(
        (x) => !x.paymentProviderTransaction
      )
      return {
        from: t('Wallet.Currency Wallet', {
          walletCurrencyId: getCurrency(
            clientWalletTransactionWithdrawalType?.wallet.currency.id,
            isArabic
          ),
        }),
        to: paWalletTransactionWithdrawalType?.paymentAgentTransaction?.paymentAgent.name || '',
        currency: clientWalletTransactionWithdrawalType?.wallet.currency.id || '',
        amount: clientWalletTransactionWithdrawalType?.amount || 0,
        rate: clientWalletTransactionWithdrawalType?.currencyRate || 0,
      }
    case TransactionType.WalletDeposit:
      return {
        from:
          transaction.walletTransactions[0].paymentProviderTransaction?.paymentProvider
            .externalName || 'unknown',
        to: t('Wallet.Currency Wallet', {
          walletCurrencyId: getCurrency(
            transaction.walletTransactions[0].wallet.currency.id,
            isArabic
          ),
        }),
        currency: transaction.walletTransactions[0].wallet.currency.id,
        amount: transaction.walletTransactions[0].amount,
        rate: transaction.walletTransactions[0].currencyRate,
      }
    case TransactionType.WalletWithdrawal:
      return {
        from:
          transaction.walletTransactions[0].wallet.walletType.id === WalletTypeEnum.IB
            ? t('Wallet.IB Currency Wallet', {
                walletCurrencyId: getCurrency(
                  transaction.walletTransactions[0].wallet.currency.id,
                  isArabic
                ),
              })
            : t('Wallet.Currency Wallet', {
                walletCurrencyId: getCurrency(
                  transaction.walletTransactions[0].wallet.currency.id,
                  isArabic
                ),
              }),
        to:
          transaction.walletTransactions[0].paymentProviderTransaction?.paymentProvider.name || '',
        currency: transaction.walletTransactions[0].wallet.currency.id,
        amount: transaction.walletTransactions[0].amount,
        rate: transaction.walletTransactions[0].currencyRate,
      }
    case TransactionType.TransferFromTradingAccount:
    case TransactionType.InactivityTransfer:
      const fromTA = isDefaultCFDProductType
        ? transaction.tradingAccountTransactions?.[0]?.tradingAccount?.name
        : getCurrency(transaction.walletTransactions[0].wallet.currency.id, isArabic) +
          ' ' +
          t('Trading Account.Trading Account')
      return {
        from: fromTA,
        to: t('Wallet.Currency Wallet', {
          walletCurrencyId: getCurrency(
            transaction.walletTransactions[0].wallet.currency.id,
            isArabic
          ),
        }),
        currency: transaction.walletTransactions[0].wallet.currency.id,
        amount: transaction.walletTransactions[0].amount,
        rate: transaction.walletTransactions[0].currencyRate,
      }
    case TransactionType.TransferToTradingAccount:
      const toTA = isDefaultCFDProductType
        ? transaction.tradingAccountTransactions?.[0]?.tradingAccount?.name
        : getCurrency(transaction.walletTransactions[0].wallet.currency.id, isArabic) +
          ' ' +
          t('Trading Account.Trading Account')

      return {
        from: t('Wallet.Currency Wallet', {
          walletCurrencyId: getCurrency(
            transaction.walletTransactions[0].wallet.currency.id,
            isArabic
          ),
        }),
        to: toTA,
        currency: transaction.walletTransactions[0].wallet.currency.id,
        amount: transaction.walletTransactions[0].amount,
        rate: transaction.walletTransactions[0].currencyRate,
      }
    case TransactionType.TradingAccountBalanceConversion:
      const transactions = transaction.tradingAccountTransactions
      const fromTradingAccount = transactions.find((x) => x.directionType.name === 'Debit')
      const toTradingAccount = transactions.find((x) => x.directionType.name === 'Credit')

      const fromCurrency = getCurrency(fromTradingAccount?.currency.id, isArabic)
      const toCurrency = getCurrency(toTradingAccount?.currency.id, isArabic)

      return {
        from: fromCurrency + ' ' + t('Trading Account.Trading Account'),
        to: toCurrency + ' ' + t('Trading Account.Trading Account'),
        currency: toTradingAccount?.currency.id || '',
        amount: toTradingAccount?.amount || 0,
        rate: fromTradingAccount?.currencyRate || 0,
      }
    case TransactionType.IBRebate:
      const cWalletTransaction = transaction.walletTransactions.find(
        (x) => x.wallet.client.id === clientId
      )
      const externalWalletTran = transaction.walletTransactions.find(
        (x) => x.ibCommisionTransaction
      )
      return {
        from: externalWalletTran?.ibCommisionTransaction?.introducingBroker.name || '',
        to: t('Wallet.Currency Wallet', {
          walletCurrencyId: getCurrency(cWalletTransaction?.wallet.currency.id, isArabic),
        }),
        currency: cWalletTransaction?.wallet.currency.id || '',
        amount: cWalletTransaction?.amount || 0,
        rate: cWalletTransaction?.currencyRate || 1,
      }
    default:
      const tradingAccountTransactionObject = isOne(transaction.tradingAccountTransactions.length)
        ? transaction.tradingAccountTransactions[0]
        : undefined
      if (tradingAccountTransactionObject) {
        const isDebit = tradingAccountTransactionObject.directionType.name === 'Debit'
        const isCredit = tradingAccountTransactionObject.directionType.name === 'Credit'
        if (isDebit) {
          return {
            from: tradingAccountTransactionObject.tradingAccount.name,
            to: 'Tickmill',
            currency: tradingAccountTransactionObject.currency.id,
            amount: tradingAccountTransactionObject.amount,
            rate: tradingAccountTransactionObject.currencyRate,
          }
        }
        if (isCredit) {
          return {
            from: 'Tickmill',
            to: tradingAccountTransactionObject.tradingAccount.name,
            currency: tradingAccountTransactionObject.currency.id,
            amount: tradingAccountTransactionObject.amount,
            rate: tradingAccountTransactionObject.currencyRate,
          }
        }
      }
      const walletTransaction = transaction.walletTransactions.find(
        (x) => x.wallet.client.id === clientId
      )

      const externalWalletTransaction = transaction.walletTransactions.find(
        (x) => x.id !== walletTransaction?.id
      )

      const paymentProvider =
        transaction.type.id === TransactionType.Reverse
          ? (walletTransaction || externalWalletTransaction)?.paymentProviderTransaction
              ?.paymentProvider
          : undefined
      const counterparty = paymentProvider?.externalName || paymentProvider?.name || 'Tickmill'

      const isDebit =
        (walletTransaction || externalWalletTransaction)?.directionType.name === 'Debit'
      const isCredit =
        (walletTransaction || externalWalletTransaction)?.directionType.name === 'Credit'

      if (isDebit) {
        return {
          from:
            getCurrency(
              (walletTransaction || externalWalletTransaction)?.wallet.currency.id,
              isArabic
            ) + getWalletText(),
          to: counterparty,
          currency: (walletTransaction || externalWalletTransaction)?.wallet.currency.id ?? '',
          amount: (walletTransaction || externalWalletTransaction)?.amount ?? 0,
          rate: (walletTransaction || externalWalletTransaction)?.currencyRate ?? 0,
        }
      } else if (isCredit) {
        return {
          from: counterparty,
          to: t('Wallet.Currency Wallet', {
            walletCurrencyId: getCurrency(
              (walletTransaction || externalWalletTransaction)?.wallet.currency.id,
              isArabic
            ),
          }),
          currency: (walletTransaction || externalWalletTransaction)?.wallet.currency.id ?? '',
          amount: (walletTransaction || externalWalletTransaction)?.amount ?? 0,
          rate: (walletTransaction || externalWalletTransaction)?.currencyRate ?? 0,
        }
      }
      return {
        from: '',
        to: '',
        currency: 'USD',
        amount: 0,
        rate: 0,
      }
  }
}

// REFERENCE - CAR-103 (https://jira.tickmill.com/browse/CAR-103)
export function getIBTransactionFieldsByTransactionType(
  type: TransactionType,
  transaction: TransactionDto,
  isArabic: boolean,
  clientId?: string
): TransactionFields {
  const clientWalletTransaction = transaction.walletTransactions.find(
    (x) => !x.paymentProviderTransaction
  )

  switch (type) {
    case TransactionType.WalletTransfer:
      const fromWallet = transaction.walletTransactions.find(
        (x) => x.directionType.name === 'Debit'
      )
      const toWallet = transaction.walletTransactions.find((x) => x.directionType.name === 'Credit')
      return {
        from: t('Wallet.IB Currency Wallet', {
          walletCurrencyId: getCurrency(fromWallet?.wallet.currency.id, isArabic),
        }),
        to:
          toWallet?.wallet.walletType.id === WalletTypeEnum.IB
            ? t('Wallet.IB Currency Wallet', {
                walletCurrencyId: getCurrency(toWallet?.wallet.currency.id, isArabic),
              })
            : t('Wallet.Currency Wallet', {
                walletCurrencyId: getCurrency(toWallet?.wallet.currency.id, isArabic),
              }),
        currency: toWallet?.wallet.currency.id || '',
        amount: toWallet?.amount || 0,
        rate: toWallet?.currencyRate || 0,
      }

    case TransactionType.DepositFromPaymentAgent:
      const paWalletTransaction = transaction.walletTransactions.find(
        (x) => !!x.paymentAgentTransaction
      )

      const isIB = clientWalletTransaction?.wallet.walletType.id === WalletTypeEnum.CFD

      return {
        from: paWalletTransaction?.paymentAgentTransaction?.paymentAgent.name || '',
        to: isIB
          ? t('Wallet.IB Currency Wallet', {
              walletCurrencyId: getCurrency(clientWalletTransaction?.wallet.currency.id, isArabic),
            })
          : t('Wallet.Currency Wallet', {
              walletCurrencyId: getCurrency(clientWalletTransaction?.wallet.currency.id, isArabic),
            }),
        currency: clientWalletTransaction?.wallet.currency.id || '',
        amount: clientWalletTransaction?.amount || 0,
        rate: clientWalletTransaction?.currencyRate || 0,
      }
    case TransactionType.WithdrawToPaymentAgent:
      const paWalletTransactionWithdrawalType = transaction.walletTransactions.find(
        (x) => !!x.paymentAgentTransaction
      )
      const clientWalletTransactionWithdrawalType = transaction.walletTransactions.find(
        (x) => !x.paymentProviderTransaction
      )

      const isIBWithdrawal = clientWalletTransaction?.wallet.walletType.id === WalletTypeEnum.CFD

      return {
        from: paWalletTransactionWithdrawalType?.paymentAgentTransaction?.paymentAgent.name || '',
        to: isIBWithdrawal
          ? t('Wallet.IB Currency Wallet', {
              walletCurrencyId: getCurrency(
                clientWalletTransactionWithdrawalType?.wallet.currency.id,
                isArabic
              ),
            })
          : t('Wallet.Currency Wallet', {
              walletCurrencyId: getCurrency(
                clientWalletTransactionWithdrawalType?.wallet.currency.id,
                isArabic
              ),
            }),
        currency: clientWalletTransactionWithdrawalType?.wallet.currency.id || '',
        amount: clientWalletTransactionWithdrawalType?.amount || 0,
        rate: clientWalletTransactionWithdrawalType?.currencyRate || 0,
      }
    case TransactionType.WalletDeposit:
      const walletDepositTo = transaction.walletTransactions[0]

      const isIBDeposit = walletDepositTo.wallet.walletType.id === WalletTypeEnum.CFD
      return {
        from:
          transaction.walletTransactions[0].paymentProviderTransaction?.paymentProvider
            .externalName || 'unknown',
        to: isIBDeposit
          ? t('Wallet.IB Currency Wallet', {
              walletCurrencyId: getCurrency(walletDepositTo.wallet.currency.id, isArabic),
            })
          : t('Wallet.Currency Wallet', {
              walletCurrencyId: getCurrency(walletDepositTo.wallet.currency.id, isArabic),
            }),
        currency: walletDepositTo.wallet.currency.id,
        amount: walletDepositTo.amount,
        rate: walletDepositTo.currencyRate,
      }
    case TransactionType.WalletWithdrawal:
      const walletWithdrawalFrom = transaction.walletTransactions[0]
      return {
        from: t('Wallet.IB Currency Wallet', {
          walletCurrencyId: getCurrency(walletWithdrawalFrom.wallet.currency.id, isArabic),
        }),
        to: walletWithdrawalFrom.paymentProviderTransaction?.paymentProvider.name || '',
        currency: walletWithdrawalFrom.wallet.currency.id,
        amount: walletWithdrawalFrom.amount,
        rate: walletWithdrawalFrom.currencyRate,
      }
    case TransactionType.TransferFromTradingAccount:
      const transferFromTAWalletTransaction = transaction.walletTransactions[0]

      const isIBTransferFromTAWalletTransaction =
        transferFromTAWalletTransaction.wallet.walletType.id === WalletTypeEnum.CFD

      return {
        from: transaction.tradingAccountTransactions?.[0]?.tradingAccount?.name ?? '',
        to: isIBTransferFromTAWalletTransaction
          ? t('Wallet.IB Currency Wallet', {
              walletCurrencyId: getCurrency(
                transferFromTAWalletTransaction.wallet.currency.id,
                isArabic
              ),
            })
          : t('Wallet.Currency Wallet', {
              walletCurrencyId: getCurrency(
                transferFromTAWalletTransaction.wallet.currency.id,
                isArabic
              ),
            }),
        currency: transferFromTAWalletTransaction.wallet.currency.id,
        amount: transferFromTAWalletTransaction.amount,
        rate: transferFromTAWalletTransaction.currencyRate,
      }
    case TransactionType.TransferToTradingAccount:
      const transferToTAWalletTransaction = transaction.walletTransactions[0]

      const isIBTransferToTAWalletTransaction =
        transferToTAWalletTransaction.wallet.walletType.id === WalletTypeEnum.CFD

      return {
        from: isIBTransferToTAWalletTransaction
          ? t('Wallet.IB Currency Wallet', {
              walletCurrencyId: getCurrency(
                transferToTAWalletTransaction.wallet.currency.id,
                isArabic
              ),
            })
          : t('Wallet.Currency Wallet', {
              walletCurrencyId: getCurrency(
                transferToTAWalletTransaction.wallet.currency.id,
                isArabic
              ),
            }),
        to: transaction.tradingAccountTransactions?.[0]?.tradingAccount?.name ?? '',
        currency: transferToTAWalletTransaction.wallet.currency.id,
        amount: transferToTAWalletTransaction.amount,
        rate: transaction.walletTransactions[0].currencyRate,
      }
    case TransactionType.IBRebate:
      const cWalletTransaction = transaction.walletTransactions.find(
        (x) => x.wallet.client.id === clientId
      )

      return {
        from: t('Wallet.IB Currency Wallet', {
          walletCurrencyId: getCurrency(cWalletTransaction?.wallet.currency.id, isArabic),
        }),
        to: t('IB.Clients'),
        currency: cWalletTransaction?.wallet.currency.id || '',
        amount: cWalletTransaction?.amount || 0,
        rate: cWalletTransaction?.currencyRate || 1,
      }
    default:
      // If the item.walletTransactions contains more than 1 object, find the one walletTransaction object which the
      // walletTransaction.wallet.client.id == <your client id>.
      // This one becomes ClientWalletTransaction.
      // The other one becomes ExternalWalletTransaction.
      // ClientWalletPrefix = if ClientWalletTransaction->wallet->walletType->id == 10 then "IB " otherwise empty string
      // a. ClientWalletTransaction.directionType.name == "Debit"
      // From = "(ClientWalletPrefix)(ClientWalletTransaction.wallet.currency.id) Wallet"
      // To = "Tickmill"
      // Amount = ClientWalletTransaction.amount
      // Rate = ClientWalletTransaction.currencyRate
      const isWalletTransaction = transaction.walletTransactions.length > 0
      if (isWalletTransaction) {
        const clientWalletTransaction = transaction.walletTransactions.find(
          (x) => x.wallet.client.id === clientId
        )
        const isDebit = clientWalletTransaction?.directionType.name === 'Debit'
        const isCredit = clientWalletTransaction?.directionType.name === 'Credit'
        const clientWalletPrefix =
          clientWalletTransaction?.wallet.walletType.id === WalletTypeEnum.IB
        if (isDebit) {
          const from = clientWalletPrefix
            ? t('Wallet.IB Currency Wallet', {
                walletCurrencyId: getCurrency(clientWalletTransaction.wallet.currency.id, isArabic),
              })
            : t('Wallet.Currency Wallet', {
                walletCurrencyId: getCurrency(clientWalletTransaction.wallet.currency.id, isArabic),
              })
          return {
            from,
            to: 'Tickmill',
            currency: clientWalletTransaction.wallet.currency.id,
            amount: clientWalletTransaction.amount,
            rate: clientWalletTransaction.currencyRate,
          }
        }
        if (isCredit) {
          return {
            from: 'Tickmill',
            to: t('Wallet.IB Currency Wallet', {
              walletCurrencyId: getCurrency(clientWalletTransaction.wallet.currency.id, isArabic),
            }),
            currency: clientWalletTransaction.wallet.currency.id,
            amount: clientWalletTransaction.amount,
            rate: clientWalletTransaction.currencyRate,
          }
        }
      }
      return {
        from: '',
        to: '',
        currency: 'USD',
        amount: 0,
        rate: 0,
      }
  }
}

// REFERENCE - CAR-104 (https://jira.tickmill.com/browse/CAR-104)
export function getPATransactionFieldsByTransactionType(
  type: TransactionType,
  transaction: TransactionDto,
  isArabic: boolean,
  clientId?: string,
  isReversed?: boolean
): TransactionFields {
  switch (type) {
    case TransactionType.WalletTransfer:
      const fromWallet = transaction.walletTransactions.find(
        (x) => x.directionType.name === 'Debit'
      )
      const toWallet = transaction.walletTransactions.find((x) => x.directionType.name === 'Credit')
      const hasFromIBPrefix = fromWallet?.wallet.walletType.id === WalletTypeEnum.PAYMENT_AGENT
      const hasToIBPrefix = toWallet?.wallet.walletType.id === WalletTypeEnum.PAYMENT_AGENT

      return {
        from: hasFromIBPrefix
          ? t('Wallet.PA Currency Wallet', {
              walletCurrencyId: getCurrency(fromWallet?.wallet.currency.id, isArabic),
            })
          : t('Wallet.Currency Wallet', {
              walletCurrencyId: getCurrency(fromWallet?.wallet.currency.id, isArabic),
            }),
        to: hasToIBPrefix
          ? t('Wallet.PA Currency Wallet', {
              walletCurrencyId: getCurrency(toWallet?.wallet.currency.id, isArabic),
            })
          : t('Wallet.Currency Wallet', {
              walletCurrencyId: getCurrency(toWallet?.wallet.currency.id, isArabic),
            }),
        currency: toWallet?.wallet.currency.id || '',
        amount: toWallet?.amount || 0,
        rate: toWallet?.currencyRate || 0,
      }

    case TransactionType.DepositFromPaymentAgent:
      const paWalletTransaction = transaction.walletTransactions.find(
        (x) => !!x.paymentAgentTransaction
      )
      const clientWalletTransaction = transaction.walletTransactions.find(
        (x) => !x.paymentAgentTransaction
      )

      return {
        from: paWalletTransaction?.paymentAgentTransaction?.paymentAgent.name || '',
        to: clientWalletTransaction?.wallet.name || '',
        currency: clientWalletTransaction?.wallet.currency.id || '',
        amount: clientWalletTransaction?.amount || 0,
        rate: clientWalletTransaction?.currencyRate || 0,
      }
    case TransactionType.WithdrawToPaymentAgent:
      const paWalletTransactionWithdrawalType = transaction.walletTransactions
        .filter((x) => x.directionType.name === (isReversed ? 'Debit' : 'Credit'))
        .find((x) => !!x.paymentAgentTransaction)
      const clientWalletTransactionWithdrawalType = transaction.walletTransactions
        .filter((x) => x.directionType.name === (isReversed ? 'Credit' : 'Debit'))
        .find((x) => !x.paymentProviderTransaction)

      return {
        from: clientWalletTransactionWithdrawalType?.wallet.name || '',
        to: paWalletTransactionWithdrawalType?.paymentAgentTransaction?.paymentAgent.name || '',
        currency: clientWalletTransactionWithdrawalType?.wallet.currency.id || '',
        amount: clientWalletTransactionWithdrawalType?.amount || 0,
        rate: clientWalletTransactionWithdrawalType?.currencyRate || 0,
      }
    case TransactionType.WalletDeposit:
      const walletDepositTo = transaction.walletTransactions[0]
      const hasWalletDepositPrefix =
        walletDepositTo.wallet.walletType.id === WalletTypeEnum.PAYMENT_AGENT

      return {
        from:
          transaction.walletTransactions[0].paymentProviderTransaction?.paymentProvider
            .externalName || 'unknown',
        to: hasWalletDepositPrefix
          ? t('Wallet.PA Currency Wallet', {
              walletCurrencyId: getCurrency(walletDepositTo.wallet.currency.id, isArabic),
            })
          : t('Wallet.Currency Wallet', {
              walletCurrencyId: getCurrency(walletDepositTo.wallet.currency.id, isArabic),
            }),

        currency: walletDepositTo.wallet.currency.id,
        amount: walletDepositTo.amount,
        rate: walletDepositTo.currencyRate,
      }
    case TransactionType.WalletWithdrawal:
      const walletWithdrawalFrom = transaction.walletTransactions[0]
      const hasWalletWithdrawalFromPrefix =
        walletWithdrawalFrom.wallet.walletType.id === WalletTypeEnum.PAYMENT_AGENT

      return {
        from: hasWalletWithdrawalFromPrefix
          ? t('Wallet.PA Currency Wallet', {
              walletCurrencyId: getCurrency(walletWithdrawalFrom.wallet.currency.id, isArabic),
            })
          : t('Wallet.Currency Wallet', {
              walletCurrencyId: getCurrency(walletWithdrawalFrom.wallet.currency.id, isArabic),
            }),
        to: walletWithdrawalFrom.paymentProviderTransaction?.paymentProvider.name || '',
        currency: walletWithdrawalFrom.wallet.currency.id,
        amount: walletWithdrawalFrom.amount,
        rate: walletWithdrawalFrom.currencyRate,
      }
    case TransactionType.TransferFromTradingAccount:
      const transferFromTAWalletTransaction = transaction.walletTransactions[0]
      const hasTransferFromTAWalletTransactionPrefix =
        transferFromTAWalletTransaction.wallet.walletType.id === WalletTypeEnum.PAYMENT_AGENT

      return {
        from: transaction.tradingAccountTransactions?.[0]?.tradingAccount?.name ?? '',
        to: hasTransferFromTAWalletTransactionPrefix
          ? t('Wallet.PA Currency Wallet', {
              walletCurrencyId: getCurrency(
                transferFromTAWalletTransaction.wallet.currency.id,
                isArabic
              ),
            })
          : t('Wallet.Currency Wallet', {
              walletCurrencyId: getCurrency(
                transferFromTAWalletTransaction.wallet.currency.id,
                isArabic
              ),
            }),
        currency: transferFromTAWalletTransaction.wallet.currency.id,
        amount: transferFromTAWalletTransaction.amount,
        rate: transferFromTAWalletTransaction.currencyRate,
      }
    case TransactionType.TransferToTradingAccount:
      const transferToTAWalletTransaction = transaction.walletTransactions[0]
      const hasTransferToTAWalletTransactionPrefix =
        transferToTAWalletTransaction.wallet.walletType.id === WalletTypeEnum.PAYMENT_AGENT

      return {
        from: hasTransferToTAWalletTransactionPrefix
          ? t('Wallet.PA Currency Wallet', {
              walletCurrencyId: getCurrency(
                transferToTAWalletTransaction.wallet.currency.id,
                isArabic
              ),
            })
          : t('Wallet.Currency Wallet', {
              walletCurrencyId: getCurrency(
                transferToTAWalletTransaction.wallet.currency.id,
                isArabic
              ),
            }),
        to: transaction.tradingAccountTransactions?.[0]?.tradingAccount?.name ?? '',
        currency: transferToTAWalletTransaction.wallet.currency.id,
        amount: transferToTAWalletTransaction.amount,
        rate: transaction.walletTransactions[0].currencyRate,
      }
    default:
      const tradingAccountTransactionObject = isOne(transaction.tradingAccountTransactions.length)
        ? transaction.tradingAccountTransactions[0]
        : undefined
      if (tradingAccountTransactionObject) {
        const isDebit = tradingAccountTransactionObject.directionType.name === 'Debit'
        const isCredit = tradingAccountTransactionObject.directionType.name === 'Credit'
        if (isDebit) {
          return {
            from: tradingAccountTransactionObject.tradingAccount.name,
            to: 'Tickmill',
            currency: tradingAccountTransactionObject.currency.id,
            amount: tradingAccountTransactionObject.amount,
            rate: tradingAccountTransactionObject.currencyRate,
          }
        }
        if (isCredit) {
          return {
            from: 'Tickmill',
            to: tradingAccountTransactionObject.tradingAccount.name,
            currency: tradingAccountTransactionObject.currency.id,
            amount: tradingAccountTransactionObject.amount,
            rate: tradingAccountTransactionObject.currencyRate,
          }
        }
      }
      const walletTransaction =
        transaction.walletTransactions.length > 1
          ? transaction.walletTransactions.find((x) => x.wallet.client.id === clientId)
          : undefined
      const externalWalletTransaction = transaction.walletTransactions.find(
        (x) => x.id !== walletTransaction?.id
      )
      if (walletTransaction) {
        const hasPaPrefix = walletTransaction.wallet.walletType.id === WalletTypeEnum.PAYMENT_AGENT

        return {
          from: hasPaPrefix
            ? t('Wallet.PA Currency Wallet', {
                walletCurrencyId: getCurrency(walletTransaction.wallet.currency.id, isArabic),
              })
            : t('Wallet.Currency Wallet', {
                walletCurrencyId: getCurrency(walletTransaction.wallet.currency.id, isArabic),
              }),
          to: 'Tickmill',
          currency: walletTransaction.wallet.currency.id,
          amount: walletTransaction.amount,
          rate: walletTransaction.currencyRate,
        }
      } else if (externalWalletTransaction) {
        const hasPaPrefix =
          externalWalletTransaction.wallet.walletType.id === WalletTypeEnum.PAYMENT_AGENT

        return {
          from: 'Tickmill',
          to: hasPaPrefix
            ? t('Wallet.PA Currency Wallet', {
                walletCurrencyId: getCurrency(
                  externalWalletTransaction.wallet.currency.id,
                  isArabic
                ),
              })
            : t('Wallet.Currency Wallet', {
                walletCurrencyId: getCurrency(
                  externalWalletTransaction.wallet.currency.id,
                  isArabic
                ),
              }),
          currency: externalWalletTransaction.wallet.currency.id,
          amount: externalWalletTransaction.amount,
          rate: externalWalletTransaction.currencyRate,
        }
      }
      return {
        from: '',
        to: '',
        currency: 'USD',
        amount: 0,
        rate: 0,
      }
  }
}

export const isTransactionTradingAccountBalanceConversionType = (
  value: TransactionType
): boolean => {
  return value === TransactionType.TradingAccountBalanceConversion
}

export const isTradingAccountTransactionType = (value: TransactionType): boolean => {
  return (
    value === TransactionType.TradingAccountBalanceConversion ||
    value === TransactionType.TransferToTradingAccount ||
    value === TransactionType.TransferFromTradingAccount
  )
}

export const sleep = <T>(interval = 3000): Promise<T> =>
  new Promise((resolve) => setTimeout(resolve, interval))

export const getCreditTransaction = (tt: TransactionType, tx: TransactionDto) => {
  switch (tt) {
    case TransactionType.TradingAccountBalanceConversion:
      return tx.tradingAccountTransactions.find((t) => t.directionType.name === 'Credit')
    case TransactionType.WalletTransfer:
    case TransactionType.WalletDeposit:
    case TransactionType.WalletWithdrawal:
      return tx.walletTransactions.find((t) => t.directionType.name === 'Credit')
    default:
      return undefined
  }
}

export const getDebitTransaction = (tt: TransactionType, tx: TransactionDto) => {
  switch (tt) {
    case TransactionType.TradingAccountBalanceConversion:
      return tx.tradingAccountTransactions.find((t) => t.directionType.name === 'Debit')
    case TransactionType.WalletTransfer:
    case TransactionType.WalletDeposit:
    case TransactionType.WalletWithdrawal:
      return tx.walletTransactions.find((t) => t.directionType.name === 'Debit')
    default:
      return undefined
  }
}

interface TransactionStatusTooltip {
  title: string
  text: string
}

export const getTransactionStatusAlertMessage = (status: TransactionStatus): string | null => {
  switch (status) {
    case TransactionStatus.Completed:
      return 'TransactionAlert.Completed'
    case TransactionStatus.Cancelled:
      return 'TransactionAlert.Cancelled'
    case TransactionStatus.Declined:
      return 'TransactionAlert.Declined'
    case TransactionStatus.Failed:
      return 'TransactionAlert.Failed'
    case TransactionStatus.Reversed:
      return 'TransactionAlert.Reversed'
    case TransactionStatus.AwaitingApproval:
      return 'TransactionAlert.AwaitingApproval'
    case TransactionStatus.Pending:
      return 'TransactionAlert.Pending'
    default:
      return null
  }
}

export const getTooltipDataForStatus = (tx: TransactionDto): TransactionStatusTooltip | null => {
  const { state, type } = tx
  const valid =
    type.id === TransactionType.WalletWithdrawal ||
    type.id === TransactionType.WithdrawToPaymentAgent
  if (!valid) return null
  switch (state.id) {
    case TransactionStatus.AwaitingApproval:
      return {
        title: t('AwaitingApproval'),
        text: t('We are on it! Most withdrawals are processed within a few hours'),
      }
    case TransactionStatus.Pending:
      return { title: t('Pending'), text: t('Almost there! Your withdrawal is being finalised') }
    default:
      return null
  }
}

const getLatestTransactionStatusUpdateDate = (
  statusHistory: TransactionStateHistoryDto | undefined
): string | undefined =>
  statusHistory?.history?.reduce<string | undefined>((latest, item) => {
    if (!latest) return item.createdDate
    return new Date(item.createdDate) > new Date(latest) ? item.createdDate : latest
  }, undefined)

export const getTransactionStatusLastUpdatedDate = (
  transaction: TransactionDto | null | undefined,
  statusHistory: TransactionStateHistoryDto | undefined
): string => getLatestTransactionStatusUpdateDate(statusHistory) || transaction?.createdDate || ''
